<script setup lang="ts">
import {useDialogPluginComponent} from 'quasar'
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import Translator from "@/utils/common/Translator.ts";
import ReferenceDialogSearcherView from "@/components/EditView/ReferenceDialogSearcherView.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import DialogButtons from "@/utils/forms/DialogButtons.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import {computed, ref, Ref, toValue, useTemplateRef} from "vue";
import ArrowIcon from "@/assets/editViewIcons/ArrowIcon.vue";
import QuickCreate from "@/components/EditView/QuickCreate.vue";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import EditIcon from "@/assets/editViewIcons/EditIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import BaseEntityDTO = namaTypes.BaseEntityDTO;

const props = withDefaults(defineProps<{
  fieldUtil?: FieldWidgetUtil,
  field?: namaTypes.EditScreenField | namaTypes.CompositeEditScreenField | namaTypes.GUIActionPlaceholder | namaTypes.EditScreenGridColumn,
  isInNewMode?: boolean,
  isFieldEnabled?: boolean,
  event?: MouseEvent & KeyboardEvent,
  newObject?: Record<string, any>
}>(), {
  isFieldEnabled: true
});

const util = props.fieldUtil as ReferenceFieldWidgetUtil | ReferenceGridFieldWidgetUtil;
const {compactMode} = storeToRefs(useNamaFlagsStore());

const editPageIndex = 1;
const listPageIndex = 0;
const event = props.event;

function getCurrentPageIndex() {
  if (props.isInNewMode)
    return editPageIndex;
  if (event?.altKey || event?.key == "F7" || event?.key == "F8")
    return editPageIndex;
  else
    return listPageIndex;
}

const maximize = ref(false);
const currentPageIndex: Ref<number> = ref(getCurrentPageIndex());
const isEditRecord: Ref<boolean> = ref(getCurrentPageIndex() === editPageIndex && !!toValue(util.formattedValue()) && event?.key !== "F8");
const quickCreateRef = useTemplateRef<InstanceType<typeof quickCreateRef>>("quickCreateRef");
const {dialogRef, onDialogCancel} = useDialogPluginComponent()
if (props.fieldUtil?.presenter)
  props.fieldUtil.presenter.closeQuickEditDialog = onDialogCancel;
const searcherViewRef = ref();
let fieldTitle = Translator.translateTitledId(util.presenter.entityType ?? util.referencedEntityType.value!, props.field);
let parentFieldTitle: string = util.presenter.fetchParentFieldTitle() + fieldTitle + ' / ';
const dialogTitle: string = getDialogTitle();
const editTitle: string = getEditTitle();
const addTitle: string = getAddTitle();
const title = computed(() => {
  if (currentPageIndex.value == 0) {
    return parentFieldTitle + dialogTitle;
  } else {
    if (isEditRecord.value)
      return parentFieldTitle + editTitle;
    else
      return parentFieldTitle + addTitle;
  }
});
const dialogHeight = computed<number>(() => {
  if (!quickCreateRef.value || !quickCreateRef.value.editViewLayoutRef || !quickCreateRef.value.editViewLayoutRef.blocksContainerRef)
    return 1900;
  let el = quickCreateRef.value.editViewLayoutRef.blocksContainerRef;
  return (el[0].clientHeight ?? 1900) + 285;
})

const loginStore = useLoginInfoStore();
const closeDialog = onDialogCancel;

function getDialogTitle(): string {
  return Translator.translateId('searchOn') + ' ' + Translator.translateId(util.referencedEntityType.value);
}

function getEditTitle(): string {
  if (util.fetchFieldValue())
    return Translator.translateId('edit') + ' ' + EditViewUtils.getEditViewTitle({} as EditPlace, util.fetchFieldValue() as BaseEntityDTO);
  return Translator.translateId('edit') + ' ' + Translator.translateId(util.referencedEntityType.value);
}

function getAddTitle(): string {
  return Translator.translateId('add') + ' ' + Translator.translateId(util.referencedEntityType.value);
}

async function onBackClick() {
  if (await quickCreateRef.value.leaveQuickCreate())
    currentPageIndex.value === 0 ? currentPageIndex.value = 1 : currentPageIndex.value = 0;
}

function onHeaderClick(isEditButton: boolean) {
  isEditRecord.value = isEditButton;
  currentPageIndex.value = 1;
}

async function onCancelClick() {
  if (!quickCreateRef.value)
    closeDialog();
  else if (await quickCreateRef.value.leaveQuickCreate())
    closeDialog();
}

async function onMaximizeClick() {
  maximize.value = !maximize.value;
}

async function onOkClick(isInsert?: boolean) {
  let shouldClose = true;
  if (currentPageIndex.value === 1 && quickCreateRef.value) {
    await FieldWidgetUtil.saveEditingFieldsAndGrids();
    shouldClose = await quickCreateRef.value.saveRecordAndSelectedIfNeeded();
  } else if (util instanceof ReferenceGridFieldWidgetUtil && searcherViewRef.value) {
    closeDialog();
    await searcherViewRef.value.onOkClicked(isInsert);
    return;
  }
  if (shouldClose)
    closeDialog();
}

async function onInsertAtEndByOrderClicked() {
  if (searcherViewRef.value)
    await searcherViewRef.value.onInsertAtEndByOrderClicked()
}

async function onInsertAtEndClicked() {
  if (searcherViewRef.value)
    await searcherViewRef.value.onInsertAtEndClicked()
}

function isDisableField(): boolean {
  if (util instanceof ReferenceGridFieldWidgetUtil)
    return !util.fieldValue.value;
  return !util.inputFieldValueRef.value;
}

function canEdit(): boolean {
  if (isDisableField())
    return false;
  return SecurityUtils.userCanEdit(util.referencedEntityType.value);
}

function canAdd(): boolean {
  return SecurityUtils.userCanCreate(util.referencedEntityType.value);
}
</script>

<template>
  <q-dialog @onHide="()=>util.presenter.closeQuickEditDialog=undefined" persistent ref="dialogRef" @hide="onCancelClick" v-if="loginStore.isLoggedIn"
            :full-height="currentPageIndex === 0" :full-width="currentPageIndex === 1 || maximize"
            @keydown.esc="onCancelClick" :data-entity-type="util.presenter.entityType">
    <q-card
      :style="{height: dialogHeight + 'px'}"
      :class="[currentPageIndex === 0 ? 'tw-h-full' : 'tw-h-[1950px] tw-max-h-full']"
      class="tw-flex tw-flex-col tw-w-full tw-shadow-none tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
      <q-bar
        class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
        <div>{{ title }}</div>
        <div class="tw-h-fit tw-flex tw-flex-row tw-gap-2">
          <q-btn dense flat @click="onMaximizeClick" :title="Translator.translateId( maximize ? 'minimize' : 'maximize')" class="tw-h-fit tw-rounded">
            <NamaIcon default-icon="nm-win-maximize" class="tw-size-5"/>
          </q-btn>
          <q-btn dense flat @click="onCancelClick" :title="Translator.translateId( 'close')" class="tw-h-fit tw-rounded">
            <NamaIcon default-icon="nm-win-close" class="tw-size-5"/>
          </q-btn>
        </div>
      </q-bar>
      <q-card-section class="tw-grow tw-flex tw-flex-col tw-p-0">
        <div class="tw-h-fit tw-flex tw-flex-row mobile:tw-flex-col tw-justify-between tw-pt-1 tw-px-3">
          <div class="tw-pb-0 tw-flex tw-gap-3">
            <q-btn
              v-if="currentPageIndex == 1"
              :dense="compactMode"
              @click="onBackClick"
              flat
              no-caps
              class="tw-h-8 tw-bg-transparent tw-text-primary-light dark:tw-text-primary-dark tw-text-sm tw-font-normal tw-px-2 tw-py-1.5 tw-min-h-fit tw-rounded-lg">
              <div class="tw-pr-2">
                <ArrowIcon class="tw-size-4" :class="{'tw-rotate-180':Translator.isLanguageRTL()}"/>
              </div>
              <span>{{ Translator.translateId('backTo') + ' ' + dialogTitle }}</span>
            </q-btn>
          </div>
          <div v-if="currentPageIndex == 0" class="tw-flex tw-gap-3 tw-pb-1">
            <q-btn
              v-if="canAdd()"
              :dense="compactMode"
              @click="onHeaderClick(false)"
              no-caps
              class="tw-h-8 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-deep-dark tw-text-primary-light dark:tw-text-white tw-text-sm tw-font-normal tw-px-2 tw-py-1.5 tw-min-h-fit tw-rounded-lg">
              <div class="tw-pr-2">
                <AddRecordIcon class="tw-size-[18px] dark:!tw-fill-white"/>
              </div>
              <span>{{ addTitle }}</span>
            </q-btn>
            <q-btn
              v-if="canEdit()"
              :dense="compactMode"
              @click="onHeaderClick(true)"
              no-caps
              class="tw-h-8 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-deep-dark tw-text-primary-light dark:tw-text-white tw-text-sm tw-font-normal tw-px-2 tw-py-1.5 tw-min-h-fit tw-rounded-lg">
              <div class="tw-pr-2">
                <EditIcon class="tw-size-[18px] !tw-fill-primary-light dark:!tw-fill-white"/>
              </div>
              <span>{{ editTitle }}</span>
            </q-btn>
          </div>
        </div>
        <q-tab-panels animated v-model="currentPageIndex" class="tw-grow tw-bg-namaBackground-bright-light tw-rounded dark:tw-bg-namaBackground-bright-dark">
          <q-tab-panel :key="0" :name="0" class="tw-h-full tw-pt-1 tw-p-3">
            <ReferenceDialogSearcherView ref="searcherViewRef" :fieldUtil="util" :isFieldEnabled="isFieldEnabled" @closeDialog="onCancelClick"/>
          </q-tab-panel>
          <q-tab-panel :key="1" :name="1" class="tw-h-full tw-flex tw-flex-col tw-px-2 tw-py-0">
            <QuickCreate ref="quickCreateRef" :fieldUtil="util" :newObject="newObject" :isFieldEnabled="isFieldEnabled"
                         :isEditRecord="isEditRecord && !isInNewMode" :parentFieldTitle="parentFieldTitle"/>
          </q-tab-panel>
        </q-tab-panels>
      </q-card-section>
      <q-card-section
        class="tw-w-full tw-h-fit tw-flex tw-pt-1 tw-pb-4 mobile:tw-pb-1 tw-flex-row tw-gap-3 tw-overflow-x-auto tw-overflow-y-hidden desktop:tw-justify-center">
        <template v-if="util instanceof ReferenceGridFieldWidgetUtil && currentPageIndex == 0">
          <q-btn
            :label="Translator.translateId('insertRowsAtEndByOrder')"
            @click="onInsertAtEndByOrderClicked"
            @keydown.enter="onInsertAtEndByOrderClicked"
            :dense="compactMode"
            class="tw-h-10 tw-bg-primary-light tw-px-2.5 dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
          <q-btn
            :label="Translator.translateId('insertRowAtEnd')"
            @click="onInsertAtEndClicked"
            @keydown.enter="onInsertAtEndClicked"
            :dense="compactMode"
            class="tw-h-10 tw-bg-primary-light tw-px-2.5 dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
          <q-btn
            :label="Translator.translateId('insertRow')"
            @click="onOkClick(true)"
            @keydown.enter="onOkClick(true)"
            :dense="compactMode"
            class="tw-h-10 tw-bg-primary-light tw-px-2.5 dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        </template>
        <template v-if="util instanceof ReferenceGridFieldWidgetUtil || currentPageIndex === 1">
          <q-btn
            :label="Translator.translateId(DialogButtons.Ok)"
            @click="onOkClick(false)"
            @keydown.enter="onOkClick(false)"
            :dense="compactMode"
            class="tw-h-10 tw-bg-primary-light tw-px-2.5 dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        </template>
        <q-btn
          outline
          :label="Translator.translateId(DialogButtons.Cancel)"
          @click="onCancelClick"
          :dense="compactMode"
          class="tw-h-10 tw-text-namaGray tw-px-2.5 dark:tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>

</style>
